/**
 * Content status scoring for the Venue Overview panel.
 *
 * Answers one question for a venue admin: which content-bearing fields on this
 * venue are filled in, and which still need attention? Groups mirror the
 * sections of the venue edit form so a low score points at the section to open.
 *
 * Scope note: only fields the venue edit form can actually set are scored, so
 * every gap shown is one the admin can close.
 */

/**
 * Structural shape this module needs from a venue. `VenueData` (the inferred
 * `venue.getById` output) satisfies it, and tests can build fixtures without
 * reconstructing the full tRPC payload.
 */
export interface ContentStatusInput {
  description?: string | null;
  descriptionSource?: string | null;
  phone?: string | null;
  website?: string | null;
  email?: string | null;
  operatingHours?: unknown;
  priceRange?: number | null;
  primaryAtmosphere?: string | null;
  reservationPolicy?: string | null;
  bookingUrl?: string | null;
  socialMedia?: {
    instagram?: string | null;
    facebook?: string | null;
    youtube?: string | null;
    tiktok?: string | null;
  } | null;
  amenities?: readonly unknown[] | null;
  foodAvailableForPurchase?: boolean | null;
  foodDescription?: string | null;
  logoImageId?: string | null;
  heroImageIds?: readonly unknown[] | null;
  imageIds?: readonly unknown[] | null;
}

export interface ContentStatusItem {
  key: string;
  label: string;
  isSet: boolean;
  /** Extra context shown when the field is set, e.g. "3 images". */
  detail?: string;
  /** Field's value came from AI, so it is filled in but not venue-verified. */
  isAiGenerated?: boolean;
}

export interface ContentStatusGroup {
  key: string;
  label: string;
  items: ContentStatusItem[];
  setCount: number;
  total: number;
}

export interface ContentStatus {
  groups: ContentStatusGroup[];
  setCount: number;
  total: number;
  /** 0-100, rounded. */
  percentComplete: number;
}

const hasText = (value: string | null | undefined): boolean =>
  typeof value === 'string' && value.trim().length > 0;

/**
 * Operating hours are stored as a `Mixed` map keyed by day, so an empty object
 * is a real possibility and must not count as "set".
 */
const hasOperatingHours = (value: unknown): boolean =>
  typeof value === 'object' && value !== null && Object.keys(value).length > 0;

const countOf = (value: readonly unknown[] | null | undefined): number => value?.length ?? 0;

const toGroup = (key: string, label: string, items: ContentStatusItem[]): ContentStatusGroup => ({
  key,
  label,
  items,
  setCount: items.filter((item) => item.isSet).length,
  total: items.length,
});

export const buildContentStatus = (venue: ContentStatusInput): ContentStatus => {
  const amenityCount = countOf(venue.amenities);
  const heroCount = countOf(venue.heroImageIds);
  const galleryCount = countOf(venue.imageIds);

  const essentials: ContentStatusItem[] = [
    {
      key: 'description',
      label: 'Description',
      isSet: hasText(venue.description),
      isAiGenerated: hasText(venue.description) && venue.descriptionSource === 'ai_generated',
    },
    { key: 'phone', label: 'Phone', isSet: hasText(venue.phone) },
    { key: 'website', label: 'Website', isSet: hasText(venue.website) },
    { key: 'email', label: 'Email', isSet: hasText(venue.email) },
    {
      key: 'socialMedia',
      label: 'Social Media',
      isSet: Object.values(venue.socialMedia ?? {}).some((handle) => hasText(handle)),
    },
  ];

  const visitorInfo: ContentStatusItem[] = [
    {
      key: 'operatingHours',
      label: 'Operating Hours',
      isSet: hasOperatingHours(venue.operatingHours),
    },
    { key: 'priceRange', label: 'Price Range', isSet: venue.priceRange != null },
    { key: 'primaryAtmosphere', label: 'Atmosphere', isSet: venue.primaryAtmosphere != null },
    {
      key: 'amenities',
      label: 'Amenities',
      isSet: amenityCount > 0,
      detail: `${amenityCount} selected`,
    },
    {
      key: 'reservationPolicy',
      label: 'Reservation Policy',
      isSet: venue.reservationPolicy != null,
    },
    { key: 'bookingUrl', label: 'Booking URL', isSet: hasText(venue.bookingUrl) },
  ];

  // A venue that does not serve food has nothing to describe. Scoring the
  // description anyway would cap its completion below 100% forever.
  if (venue.foodAvailableForPurchase === true) {
    visitorInfo.push({
      key: 'foodDescription',
      label: 'Food Description',
      isSet: hasText(venue.foodDescription),
    });
  }

  const media: ContentStatusItem[] = [
    { key: 'logo', label: 'Logo', isSet: hasText(venue.logoImageId) },
    {
      key: 'heroImages',
      label: 'Hero Images',
      isSet: heroCount > 0,
      detail: `${heroCount} ${heroCount === 1 ? 'image' : 'images'}`,
    },
    {
      key: 'galleryImages',
      label: 'Gallery Images',
      isSet: galleryCount > 0,
      detail: `${galleryCount} ${galleryCount === 1 ? 'image' : 'images'}`,
    },
  ];

  const groups = [
    toGroup('essentials', 'Essentials', essentials),
    toGroup('visitorInfo', 'Visitor Info', visitorInfo),
    toGroup('media', 'Media', media),
  ];

  const setCount = groups.reduce((sum, group) => sum + group.setCount, 0);
  const total = groups.reduce((sum, group) => sum + group.total, 0);

  return {
    groups,
    setCount,
    total,
    percentComplete: total === 0 ? 0 : Math.round((setCount / total) * 100),
  };
};
